import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import {
  HostControlDirective,
  InputError,
  PasswordErrorRequired,
  PasswordPolicies,
  PasswordValidationService,
} from '@ebeihilfe/core';
import { ProgressBarComponent } from '@ebeihilfe/shared';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'portal-password-input',
  imports: [
    MatFormField,
    TranslateModule,
    ReactiveFormsModule,
    MatInput,
    MatIconButton,
    MatIcon,
    MatFormFieldModule,
    ProgressBarComponent,
  ],
  templateUrl: './password-input.component.html',
  hostDirectives: [HostControlDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordInputComponent implements OnInit {
  private readonly passwordValidationService = inject(PasswordValidationService);
  private readonly destroyRef = inject(DestroyRef);

  public errors = model<InputError[]>([]);
  public accessorDirective = inject(HostControlDirective);
  public autocomplete = input<string>('current-password');
  public createPasswordMode = input<boolean>(false);
  public hideRequiredMarker = input<boolean>(true);
  public hint = input<string>(_('info_text.input.password'));
  public id = input<string>('inputPassword');
  public inputmode = input<'text' | 'numeric'>('text');
  public label = input<string>(_('label.password'));
  public placeholder = input<string>();
  public showPassword = signal(false);
  public passwordRevealIcon = computed(() => (this.showPassword() ? 'visibility_off' : 'visibility'));
  public fieldType = computed(() => (this.showPassword() ? 'text' : 'password'));
  public showPlaceholder = computed(() => {
    const placeholder = this.placeholder();
    const labelFromInput = this.label();
    return !(placeholder === undefined || placeholder === labelFromInput);
  });
  public passwordRules = signal<PasswordPolicies | null>(null);

  public get shouldShowError(): boolean {
    const control = this.accessorDirective.control;
    return control && control.invalid && (control.dirty || control.touched);
  }

  public ngOnInit(): void {
    this.passwordValidationService.policies$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((policies) => {
      if (policies) {
        this.passwordRules.set(policies);
        this.updateValidatorsAndErrors(policies);
      }
    });
  }

  public togglePasswordVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  private updateValidatorsAndErrors(policies: PasswordPolicies): void {
    const control = this.accessorDirective.control;

    if (control) {
      const validators = [Validators.required];
      this.addToErrors(PasswordErrorRequired);

      const rules = policies.passwordPolicies ?? [];

      if (this.createPasswordMode() && rules.length > 0) {
        validators.push(this.passwordValidationService.createPasswordValidator(rules));
      }

      control.addValidators(validators);
      control.updateValueAndValidity();
    }
  }

  private addToErrors(error: InputError): void {
    this.errors.set([...this.errors(), error]);
  }
}
